import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import { fetchMe } from '../store/slices/sessionSlice';
import { fetchMediaList, fetchRegisterChannel } from '../store/slices/masterSlice';
import { t } from '../i18n';
import LoadingOverlay from './shared/LoadingOverlay';

interface Props {
  children: React.ReactNode;
}

/**
 * Wraps the entire app. Calls GET /api/v1/me once on mount.
 * - While loading → shows a spinner
 * - On 401 → redirects the full browser to /auth/login (full page nav, not AJAX)
 * - On success → renders children
 */
export const AuthBoundary: React.FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const sessionStatus = useAppSelector((s) => s.session.status);
  const mediaListStatus = useAppSelector((s) => s.master.mediaListStatus);
  const registerChannelStatus = useAppSelector((s) => s.master.registerChannelStatus);

  useEffect(() => {
    void dispatch(fetchMe());
  }, [dispatch]);

  useEffect(() => {
    if (sessionStatus === 'authenticated') {
      if (mediaListStatus === 'idle') void dispatch(fetchMediaList());
      if (registerChannelStatus === 'idle') void dispatch(fetchRegisterChannel());
    }
  }, [dispatch, sessionStatus, mediaListStatus, registerChannelStatus]);

  // Redirect to login in an effect — window.location mutation is a side effect
  // and must not happen during render (React's render phase must be pure).
  useEffect(() => {
    if (sessionStatus === 'unauthenticated') {
      window.location.href = `/auth/login?returnTo=${encodeURIComponent(
        window.location.pathname + window.location.search,
      )}`;
    }
  }, [sessionStatus]);

  // Network errors and other non-401/500 failures land here.
  // apiFetch handles 500 by redirecting the browser; these cases are unreachable.
  // For real network failures (TypeError: Failed to fetch), render an actionable
  // error state rather than leaving the user stuck on "Loading…" forever.
  if (sessionStatus === 'failed') {
    return (
      <div>
        <p>{t('error_message')}</p>
        <button
          onClick={() => {
            window.location.href = '/';
          }}
        >
          {t('error_reload_button')}
        </button>
      </div>
    );
  }

  if (
    sessionStatus === 'idle' ||
    sessionStatus === 'loading' ||
    sessionStatus === 'unauthenticated'
  ) {
    return <LoadingOverlay />;
  }

  return <>{children}</>;
};
